import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiRequestError } from '../api/client.ts';
import type { HorizonRemovalPreview } from '../api/endpoints.ts';
import { useOnlineStatus } from './index.ts';

/** A preview is consent for one draft in one live context, never a queued write. */
export function useHorizonChange<Draft extends object, Preview extends HorizonRemovalPreview, Result>({
  draft,
  context,
  writable,
  preview,
  save,
  onSaved,
}: {
  draft: Draft;
  context: string;
  writable: boolean;
  preview: (draft: Draft) => Promise<Preview>;
  save: (draft: Draft, previewToken: string) => Promise<Result>;
  onSaved: (result: Result, submitted: Draft) => void;
}) {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const [phase, setPhase] = useState<'idle' | 'previewing' | 'saving'>('idle');
  const [confirmation, setConfirmation] = useState<{ draft: Draft; context: string; preview: Preview } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const generation = useRef(0);
  const operation = useRef(0);
  const busy = useRef(false);
  const mounted = useRef(true);
  const latest = useRef({ draft, context, writable, online, preview, save, onSaved });
  latest.current = { draft, context, writable, online, preview, save, onSaved };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current++;
    };
  }, []);

  useEffect(() => {
    generation.current++;
    if (confirmation) setError(t('recurringHorizon.stale'));
    setConfirmation(null);
    // A sent save cannot be recalled; a pending preview can be abandoned.
    if (phase !== 'saving') {
      operation.current++;
      busy.current = false;
      setPhase('idle');
    }
  }, [draft, context, writable, online]);

  function failure(cause: unknown) {
    setError(cause instanceof ApiRequestError ? cause.message : t('recurringHorizon.failed'));
    setFieldErrors(cause instanceof ApiRequestError ? cause.fields : {});
  }

  async function commit(input: Draft, source: string, checked: Preview) {
    const current = latest.current;
    if (current.draft !== input || current.context !== source || !current.writable || !current.online) {
      setConfirmation(null);
      setError(t('recurringHorizon.stale'));
      return;
    }
    busy.current = true;
    const savingOperation = ++operation.current;
    setPhase('saving');
    setError(null);
    setFieldErrors({});
    try {
      const result = await current.save(input, checked.previewToken);
      if (!mounted.current) return;
      setConfirmation(null);
      // A response from a previous company or location must never be adopted.
      if (latest.current.context === source) latest.current.onSaved(result, input);
    } catch (cause) {
      if (!mounted.current || latest.current.context !== source) return;
      if (cause instanceof ApiRequestError && cause.status === 409) {
        setConfirmation(null);
        setError(t('recurringHorizon.stale'));
        busy.current = false;
        await check(false);
      } else failure(cause);
    } finally {
      if (mounted.current && operation.current === savingOperation) {
        busy.current = false;
        setPhase('idle');
      }
    }
  }

  async function check(allowImmediateSave = true) {
    if (busy.current || !latest.current.writable || !latest.current.online) return;
    const input = latest.current.draft;
    const source = latest.current.context;
    const request = ++generation.current;
    const previewOperation = ++operation.current;
    busy.current = true;
    setPhase('previewing');
    setConfirmation(null);
    if (allowImmediateSave) setError(null);
    setFieldErrors({});
    try {
      const checked = await latest.current.preview(input);
      if (!mounted.current || request !== generation.current) return;
      busy.current = false;
      if (checked.removeCount > 0 || !allowImmediateSave) {
        setConfirmation({ draft: input, context: source, preview: checked });
      } else await commit(input, source, checked);
    } catch (cause) {
      if (mounted.current && request === generation.current) failure(cause);
    } finally {
      if (mounted.current && request === generation.current && operation.current === previewOperation) {
        busy.current = false;
        setPhase('idle');
      }
    }
  }

  return {
    phase,
    online,
    confirmation: confirmation?.preview ?? null,
    error,
    fieldErrors,
    clear: () => {
      generation.current++;
      setConfirmation(null);
      setError(null);
      setFieldErrors({});
      if (phase !== 'saving') {
        operation.current++;
        busy.current = false;
        setPhase('idle');
      }
    },
    submit: () => void check(),
    confirm: () => {
      if (!busy.current && confirmation) void commit(confirmation.draft, confirmation.context, confirmation.preview);
    },
    cancel: () => {
      if (phase === 'saving') return;
      generation.current++;
      operation.current++;
      busy.current = false;
      setPhase('idle');
      setConfirmation(null);
      setError(null);
    },
  };
}
